// src/pages/Profile/Profile.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer, toast } from "react-toastify";
import {
  FaBell,
  FaCamera,
  FaCheckCircle,
  FaCloudUploadAlt,
  FaEnvelope,
  FaExclamationTriangle,
  FaImage,
  FaInfoCircle,
  FaLink,
  FaPen,
  FaSave,
  FaShieldAlt,
  FaSyncAlt,
  FaTimes,
  FaUser,
  FaUserCircle,
} from "react-icons/fa";

import backgroundImage from "./HeaderSlider_02.jpg";
import useScrollToTop from "./../../hooks/useScollToTop";
import {
  getUserById,
  updateUserProfile,
  toggleEmailNotifications,
} from "./../../services/userServices";
import { getRoleFromRoleId } from "./../../utils/roleHelper";

import {
  uploadUserPhoto,
  cropUserPhoto,
  toAbsoluteUserPhotoUrl,
} from "./../../services/userPhotoServices";

import CropPhotoModal, {
  toMasterUrl,
} from "./../../components/CropPhotoModal/CropPhotoModal";

const DEFAULT_AVATAR = "/img/user_avatar.jpg";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const ROLE_META = {
  1: {
    label: "Administrateur",
    badge:
      "border-red-200 bg-red-50 text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300",
    icon: FaShieldAlt,
  },
  2: {
    label: "Utilisateur",
    badge:
      "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300",
    icon: FaUser,
  },
  3: {
    label: "Enseignant",
    badge:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
    icon: FaUserCircle,
  },
};

const withBust = (url, version) => {
  if (!url) return url;
  return url.includes("?") ? `${url}&v=${version}` : `${url}?v=${version}`;
};

const ActionButton = ({
  children,
  onClick,
  type = "button",
  icon: Icon,
  variant = "primary",
  disabled = false,
}) => {
  const variants = {
    primary:
      "bg-primary text-white shadow-lg hover:-translate-y-0.5 hover:brightness-110",
    secondary:
      "border border-slate-200 bg-white text-slate-700 shadow-sm hover:-translate-y-0.5 hover:border-primary/30 hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-slate-200",
    danger:
      "border border-red-200 bg-red-50 text-red-700 hover:-translate-y-0.5 hover:bg-red-100 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300",
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-bold transition",
        variants[variant],
        "focus:outline-none focus:ring-4 focus:ring-primary/20",
        disabled &&
          "cursor-not-allowed opacity-60 hover:translate-y-0 hover:brightness-100"
      )}
    >
      {Icon && <Icon className="text-sm" />}
      {children}
    </button>
  );
};

const Field = ({ label, name, value, onChange, autoComplete }) => {
  return (
    <div>
      <label className="mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </label>

      <input
        type="text"
        name={name}
        value={value || ""}
        onChange={onChange}
        autoComplete={autoComplete}
        className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-primary focus:ring-4 focus:ring-primary/10 dark:border-white/10 dark:bg-slate-950/70 dark:text-white"
      />
    </div>
  );
};

const Profile = () => {
  useScrollToTop();

  const [user, setUser] = useState({
    id: "",
    nom: "",
    prenom: "",
    email: "",
    photoURL: "",
    role_id: 2,
    email_notifications_enabled: false,
  });

  const userRole = getRoleFromRoleId(user?.role_id);

  const [isEditing, setIsEditing] = useState(false);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);

  const [uploadMode, setUploadMode] = useState("url");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [outputFormat, setOutputFormat] = useState("webp");

  const fileInputRef = useRef(null);

  const [imgVersion, setImgVersion] = useState(0);
  const [imgOk, setImgOk] = useState(true);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [isRefreshingRole, setIsRefreshingRole] = useState(false);
  const [loading, setLoading] = useState(true);

  const roleMeta = ROLE_META[user.role_id] || {
    label: `Rôle inconnu (${user.role_id})`,
    badge:
      "border-slate-200 bg-slate-50 text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200",
    icon: FaUser,
  };

  const RoleIcon = roleMeta.icon;

  const absolutePhoto = toAbsoluteUserPhotoUrl(user.photoURL);
  const previewSrc = absolutePhoto
    ? withBust(absolutePhoto, imgVersion)
    : withBust(DEFAULT_AVATAR, imgVersion);

  const displayName = useMemo(() => {
    const fullName = `${user.prenom || ""} ${user.nom || ""}`.trim();
    return fullName || "Profil utilisateur";
  }, [user.prenom, user.nom]);

  const refreshUserProfile = async (showToastMessage = false) => {
    const localStorageUser = localStorage.getItem("userInfo");
    const userInfo = localStorageUser ? JSON.parse(localStorageUser) : null;
    const userId = userInfo?.userId;

    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const userData = await getUserById(userId);

      setUser((previous) => ({
        ...previous,
        ...userData,
        email_notifications_enabled: !!userData.email_notifications_enabled,
      }));

      setImgOk(true);
      setImgVersion((version) => version + 1);

      const currentInfo = localStorage.getItem("userInfo");
      const stored = currentInfo ? JSON.parse(currentInfo) : {};

      localStorage.setItem(
        "userInfo",
        JSON.stringify({
          ...stored,
          nom: userData.nom ?? stored.nom,
          prenom: userData.prenom ?? stored.prenom,
          photoURL: userData.photoURL ?? stored.photoURL,
          role_id: userData.role_id ?? stored.role_id,
          userId: stored.userId ?? userData.id,
        })
      );

      if (showToastMessage) {
        toast.success("Profil et rôle actualisés !");
      }
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des données de l'utilisateur:",
        error
      );

      toast.error("Erreur lors de l’actualisation du profil.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshUserProfile();
  }, []);

  const handleCloseModal = () => {
    setIsEditing(false);
    setIsPhotoModalOpen(false);
    setIsUploading(false);
    setUploadProgress(0);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setUser((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const updateLocalStorageUser = (payload = {}) => {
    const currentInfo = localStorage.getItem("userInfo");
    const stored = currentInfo ? JSON.parse(currentInfo) : {};

    localStorage.setItem(
      "userInfo",
      JSON.stringify({
        ...stored,
        ...payload,
      })
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      const token = localStorage.getItem("token");

      const updated = await updateUserProfile(user.id, user, token);

      updateLocalStorageUser({
        nom: updated.nom,
        prenom: updated.prenom,
        photoURL: updated.photoURL || user.photoURL,
        role_id: updated.role_id ?? user.role_id,
      });

      setUser((previous) => ({
        ...previous,
        ...updated,
      }));

      setIsEditing(false);

      toast.success("Profil mis à jour avec succès !");
    } catch (error) {
      console.error(
        "Erreur lors de la mise à jour des données de l'utilisateur:",
        error
      );

      toast.error("Erreur lors de la mise à jour du profil.");
    }
  };

  const openPhotoModal = () => {
    setIsPhotoModalOpen(true);
    setUploadMode("url");
    setUploadProgress(0);
    setIsUploading(false);
    setImgOk(true);
  };

  const openFilePicker = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    event.stopPropagation();

    const file = event.target.files?.[0];

    if (!file) return;

    if (!user?.id) {
      toast.error("ID utilisateur manquant.");
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);

      const response = await uploadUserPhoto(user.id, file, {
        format: outputFormat,
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;

          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );

          setUploadProgress(percent);
        },
      });

      const newPhotoUrl =
        response?.photo_url ||
        response?.photoURL ||
        response?.imageUrl ||
        response?.url ||
        response?.path ||
        response?.data?.photo_url ||
        response?.data?.photoURL ||
        response?.data?.imageUrl ||
        response?.data?.url ||
        response?.data?.path;

      if (newPhotoUrl) {
        setUser((previous) => ({
          ...previous,
          photoURL: newPhotoUrl,
        }));

        updateLocalStorageUser({
          photoURL: newPhotoUrl,
        });

        setImgOk(true);
        setImgVersion((version) => version + 1);

        toast.success("Photo uploadée et traitée.");
      } else {
        console.warn("Réponse upload inattendue:", response);
        toast.warn("Upload terminé mais URL non renvoyée.");
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error(error);
      toast.error(`Erreur upload : ${error?.message || "inconnue"}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handlePhotoSave = async (event) => {
    event.preventDefault();

    if (uploadMode === "url") {
      try {
        const token = localStorage.getItem("token");
        const updated = await updateUserProfile(user.id, { ...user }, token);

        const nextPhoto = updated.photoURL || user.photoURL;

        updateLocalStorageUser({
          photoURL: nextPhoto,
        });

        setUser((previous) => ({
          ...previous,
          photoURL: nextPhoto,
        }));

        setImgVersion((version) => version + 1);
        setIsPhotoModalOpen(false);

        toast.success("Photo de profil mise à jour !");
      } catch (error) {
        console.error(error);
        toast.error("Erreur lors de l’enregistrement de la photo.");
      }

      return;
    }

    setIsPhotoModalOpen(false);
  };

  const handleToggleEmailNotifications = async () => {
    const newValue = !user.email_notifications_enabled;

    try {
      const token = localStorage.getItem("token");

      const updatedUser = await toggleEmailNotifications(
        user.id,
        newValue,
        token
      );

      setUser((previous) => ({
        ...previous,
        email_notifications_enabled: updatedUser.email_notifications_enabled,
      }));

      toast.success(
        `Notifications par mail ${newValue ? "activées" : "désactivées"} !`
      );
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la mise à jour de la préférence mail.");
    }
  };

  const handleRefreshRole = async () => {
    try {
      setIsRefreshingRole(true);

      await refreshUserProfile(true);

      window.location.reload();
    } catch (error) {
      console.error(error);
      toast.error("Impossible de rafraîchir le rôle.");
    } finally {
      setIsRefreshingRole(false);
    }
  };

  if (loading) {
    return (
      <main className="relative min-h-screen overflow-hidden bg-slate-950 text-white">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${backgroundImage}")` }}
        />
        <div className="absolute inset-0 bg-slate-950/80" />

        <div className="relative z-10 flex min-h-screen items-center justify-center px-4 pt-[calc(var(--site-navbar-h)+48px)]">
          <div className="w-full max-w-4xl rounded-[2.5rem] border border-white/10 bg-white/10 p-8 shadow-2xl backdrop-blur-2xl">
            <div className="mx-auto h-28 w-28 animate-pulse rounded-full bg-white/20" />
            <div className="mx-auto mt-6 h-8 w-64 animate-pulse rounded-full bg-white/20" />
            <div className="mx-auto mt-4 h-5 w-80 animate-pulse rounded-full bg-white/10" />
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-950 text-white">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url("${backgroundImage}")` }}
      />

      <div className="absolute inset-0 bg-gradient-to-br from-slate-950/92 via-slate-950/75 to-slate-950/60" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:42px_42px] opacity-20" />

      <div className="absolute left-[-120px] top-24 h-80 w-80 rounded-full bg-primary/25 blur-3xl" />
      <div className="absolute bottom-[-140px] right-[-100px] h-96 w-96 rounded-full bg-blue-500/20 blur-3xl" />

      <div className="relative z-10 mx-auto flex min-h-screen max-w-6xl items-center px-4 pb-12 pt-[calc(var(--site-navbar-h)+48px)] sm:px-6 lg:px-8">
        <section className="w-full overflow-hidden rounded-[2.5rem] border border-white/10 bg-white/10 shadow-2xl backdrop-blur-2xl">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr]">
            <aside className="relative bg-slate-950/75 p-6 sm:p-8 lg:p-10">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.30),transparent_36%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.18),transparent_36%)]" />

              <div className="relative flex h-full flex-col items-center justify-center text-center">
                <div className="relative">
                  <div className="absolute inset-0 rounded-full bg-primary/30 blur-3xl" />

                  <img
                    key={imgVersion}
                    src={previewSrc}
                    alt="Avatar de l'utilisateur"
                    className="relative h-36 w-36 rounded-full border-4 border-white/20 bg-white object-cover shadow-2xl sm:h-44 sm:w-44"
                    onError={() => setImgOk(false)}
                  />

                  <button
                    type="button"
                    onClick={openPhotoModal}
                    className="absolute bottom-2 right-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-white shadow-xl transition hover:scale-105 hover:brightness-110"
                    aria-label="Modifier la photo"
                  >
                    <FaCamera />
                  </button>
                </div>

                {!imgOk && (
                  <div className="mt-4 rounded-full border border-red-500/20 bg-red-500/10 px-4 py-2 text-xs font-bold text-red-200">
                    Impossible de charger l’image.
                  </div>
                )}

                <div className="mt-7 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-white/70 backdrop-blur-xl">
                  <FaUserCircle className="text-primary" />
                  Profil utilisateur
                </div>

                <h1 className="mt-5 font-barlow text-4xl font-bold leading-tight text-white sm:text-5xl">
                  {displayName}
                </h1>

                <p className="mt-3 break-all text-sm font-semibold text-white/60">
                  {user.email}
                </p>

                <div
                  className={cn(
                    "mt-5 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-bold",
                    roleMeta.badge
                  )}
                >
                  <RoleIcon />
                  {roleMeta.label}
                </div>
              </div>
            </aside>

            <section className="relative bg-white p-6 text-slate-950 dark:bg-slate-900 dark:text-white sm:p-8 lg:p-10">
              <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />

              <div className="relative">
                <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
                  <FaShieldAlt />
                  Espace personnel
                </div>

                <h2 className="mt-5 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white">
                  Mon profil
                </h2>

                <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-500 dark:text-slate-400">
                  Gérez vos informations personnelles, votre photo de profil et
                  vos préférences de notification.
                </p>

                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  <div className="rounded-[1.7rem] border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5">
                    <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                      <FaUser />
                    </div>

                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                      Identité
                    </p>

                    <p className="mt-2 text-lg font-bold text-slate-950 dark:text-white">
                      {displayName}
                    </p>
                  </div>

                  <div className="rounded-[1.7rem] border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5">
                    <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                      <FaEnvelope />
                    </div>

                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                      Email
                    </p>

                    <p className="mt-2 break-all text-lg font-bold text-slate-950 dark:text-white">
                      {user.email || "Non renseigné"}
                    </p>
                  </div>
                </div>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                  <ActionButton icon={FaPen} onClick={() => setIsEditing(true)}>
                    Modifier les informations
                  </ActionButton>

                  <ActionButton
                    icon={FaCamera}
                    onClick={openPhotoModal}
                    variant="secondary"
                  >
                    Modifier la photo
                  </ActionButton>

                  <ActionButton
                    icon={FaSyncAlt}
                    onClick={handleRefreshRole}
                    variant="secondary"
                    disabled={isRefreshingRole}
                  >
                    {isRefreshingRole ? "Actualisation..." : "Rafraîchir le rôle"}
                  </ActionButton>
                </div>

                {(userRole === "admin" || userRole === "enseignant") && (
                  <div className="mt-8 rounded-[2rem] border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex items-start gap-4">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                          <FaBell />
                        </div>

                        <div>
                          <h3 className="font-barlow text-xl font-bold text-slate-950 dark:text-white">
                            Notifications par mail
                          </h3>

                          <p className="mt-2 text-sm leading-7 text-slate-500 dark:text-slate-400">
                            Activez cette option pour recevoir certaines
                            notifications importantes directement par email.
                            Pensez à vérifier vos spams lors du premier envoi.
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={handleToggleEmailNotifications}
                        className={cn(
                          "relative inline-flex h-8 w-16 shrink-0 items-center rounded-full transition",
                          user.email_notifications_enabled
                            ? "bg-primary"
                            : "bg-slate-300 dark:bg-slate-700"
                        )}
                        aria-pressed={!!user.email_notifications_enabled}
                      >
                        <span
                          className={cn(
                            "inline-block h-6 w-6 transform rounded-full bg-white shadow transition",
                            user.email_notifications_enabled
                              ? "translate-x-9"
                              : "translate-x-1"
                          )}
                        />
                      </button>
                    </div>
                  </div>
                )}

                <div className="mt-8 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm leading-6 text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
                  <div className="flex items-start gap-3">
                    <FaInfoCircle className="mt-1 shrink-0 text-primary" />
                    <p>
                      Après une modification importante du profil, une
                      déconnexion/reconnexion peut être nécessaire pour mettre à
                      jour l’affichage dans toute l’application.
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </section>
      </div>

      {isEditing && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/75 px-4 py-6 backdrop-blur-sm"
          onClick={handleCloseModal}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="relative w-full max-w-lg overflow-hidden rounded-[2rem] border border-white/10 bg-white p-6 shadow-2xl dark:bg-slate-950 sm:p-8"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={handleCloseModal}
              className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
              aria-label="Fermer"
            >
              <FaTimes />
            </button>

            <div className="pr-10">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-primary dark:bg-orange-500/10">
                <FaPen />
              </div>

              <h3 className="mt-5 font-barlow text-3xl font-bold text-slate-950 dark:text-white">
                Modifier le profil
              </h3>

              <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                Mettez à jour votre nom et prénom.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="mt-7 space-y-5">
              <Field
                label="Nom"
                name="nom"
                value={user.nom}
                onChange={handleChange}
                autoComplete="family-name"
              />

              <Field
                label="Prénom"
                name="prenom"
                value={user.prenom}
                onChange={handleChange}
                autoComplete="given-name"
              />

              <div className="flex flex-col gap-3 pt-3 sm:flex-row sm:justify-end">
                <ActionButton
                  icon={FaTimes}
                  onClick={handleCloseModal}
                  variant="secondary"
                >
                  Annuler
                </ActionButton>

                <ActionButton icon={FaSave} type="submit">
                  Sauvegarder
                </ActionButton>
              </div>
            </form>
          </div>
        </div>
      )}

      {isPhotoModalOpen && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/75 px-4 py-6 backdrop-blur-sm"
          onClick={() => setIsPhotoModalOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="relative max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-[2rem] border border-white/10 bg-white p-6 shadow-2xl dark:bg-slate-950 sm:p-8"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setIsPhotoModalOpen(false)}
              className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
              aria-label="Fermer"
            >
              <FaTimes />
            </button>

            <div className="pr-10">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-primary dark:bg-orange-500/10">
                <FaCamera />
              </div>

              <h3 className="mt-5 font-barlow text-3xl font-bold text-slate-950 dark:text-white">
                Photo de profil
              </h3>

              <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                Ajoutez une photo par URL ou importez une image depuis votre
                ordinateur.
              </p>
            </div>

            <form onSubmit={handlePhotoSave} className="mt-7 space-y-6">
              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => setUploadMode("url")}
                  className={cn(
                    "flex items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-sm font-bold transition",
                    uploadMode === "url"
                      ? "border-primary bg-primary text-white"
                      : "border-slate-200 bg-slate-50 text-slate-700 hover:border-primary/30 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
                  )}
                >
                  <FaLink />
                  Lien URL
                </button>

                <button
                  type="button"
                  onClick={() => setUploadMode("upload")}
                  className={cn(
                    "flex items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-sm font-bold transition",
                    uploadMode === "upload"
                      ? "border-primary bg-primary text-white"
                      : "border-slate-200 bg-slate-50 text-slate-700 hover:border-primary/30 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
                  )}
                >
                  <FaCloudUploadAlt />
                  Uploader
                </button>
              </div>

              {uploadMode === "url" ? (
                <div>
                  <label className="mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200">
                    URL de la photo
                  </label>

                  <input
                    type="text"
                    value={user.photoURL || ""}
                    onChange={(event) => {
                      setUser((previous) => ({
                        ...previous,
                        photoURL: event.target.value,
                      }));
                      setImgOk(true);
                      setImgVersion((version) => version + 1);
                    }}
                    placeholder="https://… ou /uploads/…"
                    className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10 dark:border-white/10 dark:bg-slate-950/70 dark:text-white"
                  />

                  <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                    Utilisez une URL absolue ou un chemin interne commençant par
                    /uploads/.
                  </p>
                </div>
              ) : (
                <div className="rounded-[1.7rem] border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5">
                  <input
                    ref={fileInputRef}
                    id="user-photo-file"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />

                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <ActionButton
                      icon={FaCloudUploadAlt}
                      onClick={openFilePicker}
                      variant="secondary"
                      disabled={isUploading}
                    >
                      Choisir un fichier
                    </ActionButton>

                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-slate-600 dark:text-slate-300">
                        Format
                      </span>

                      <select
                        className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 outline-none focus:border-primary dark:border-white/10 dark:bg-slate-950 dark:text-white"
                        value={outputFormat}
                        onChange={(event) => setOutputFormat(event.target.value)}
                        disabled={isUploading}
                      >
                        <option value="webp">WEBP</option>
                        <option value="jpg">JPG</option>
                        <option value="png">PNG</option>
                      </select>
                    </div>
                  </div>

                  {isUploading && (
                    <div className="mt-5">
                      <div className="h-3 overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
                        <div
                          className="h-full rounded-full bg-primary transition-all"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>

                      <p className="mt-2 text-xs font-bold text-slate-500 dark:text-slate-400">
                        {uploadProgress}%
                      </p>
                    </div>
                  )}

                  <p className="mt-4 text-xs leading-5 text-slate-500 dark:text-slate-400">
                    L’image sera traitée côté serveur en carré 400×400 et
                    optimisée.
                  </p>
                </div>
              )}

              <div className="rounded-[1.7rem] border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5">
                <div className="flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200">
                  <FaImage className="text-primary" />
                  Aperçu
                </div>

                <div className="mt-4 flex flex-col items-center gap-4 sm:flex-row">
                  <div className="h-32 w-32 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-white/10">
                    <img
                      key={imgVersion}
                      src={
                        user.photoURL
                          ? withBust(
                              toAbsoluteUserPhotoUrl(user.photoURL),
                              imgVersion
                            )
                          : withBust(DEFAULT_AVATAR, imgVersion)
                      }
                      alt="Aperçu"
                      onError={() => setImgOk(false)}
                      className="h-full w-full object-cover"
                    />
                  </div>

                  <div className="flex flex-col gap-3">
                    {user.photoURL && (
                      <ActionButton
                        icon={FaCamera}
                        onClick={() => setIsCropOpen(true)}
                        variant="secondary"
                        disabled={isUploading}
                      >
                        Recadrer / Zoom
                      </ActionButton>
                    )}

                    {!imgOk && (
                      <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-xs font-bold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
                        Image impossible à charger.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-end">
                <ActionButton
                  icon={FaTimes}
                  onClick={() => setIsPhotoModalOpen(false)}
                  variant="secondary"
                >
                  Fermer
                </ActionButton>

                <ActionButton icon={FaSave} type="submit">
                  Sauvegarder
                </ActionButton>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs leading-5 text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
                Il faudra peut-être vous déconnecter et reconnecter pour
                actualiser votre photo partout.
              </div>
            </form>
          </div>
        </div>
      )}

      <CropPhotoModal
        isOpen={isCropOpen}
        onClose={() => setIsCropOpen(false)}
        entityId={user?.id}
        imageUrl={
          user.photoURL
            ? `${toAbsoluteUserPhotoUrl(toMasterUrl(user.photoURL))}?v=${imgVersion}`
            : ""
        }
        onCropped={(newUrl) => {
          setUser((previous) => ({
            ...previous,
            photoURL: newUrl,
          }));

          updateLocalStorageUser({
            photoURL: newUrl,
          });

          setImgVersion((version) => version + 1);
          toast.success("Photo recadrée.");
        }}
        format="webp"
        cropAction={cropUserPhoto}
      />

      <ToastContainer />
    </main>
  );
};

export default Profile;